import { MicOff, Send, Loader2 } from "lucide-react";
import HoldToSpeak from "../../components/HoldToSpeak";
import AudioWave from "../../components/AudioWave";

/**
 * Qualitative chat phase — the live AI interview. The parent owns:
 *   - the messages array (chat transcript)
 *   - the MediaRecorder lifecycle (start/stop recording)
 *   - the analyser ref (drives the live waveform)
 *   - the video preview ref (used for video-enabled campaigns)
 *
 * This component is pure layout — no business logic.
 */
export default function ScreenQual({
  campaign, qIndex, messages, sending, error,
  recording, hasMic,
  draft, setDraft,
  startRecording, stopRecording,
  sendUserMessage, handlePaste,
  analyserRef, videoPreviewRef,
}) {
  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)] flex flex-col" data-testid="qual-phase">
      <div className="px-6 py-4 border-b border-[var(--rl-border)] bg-white">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Phase 2 — Voice Interview</div>
          <div className="text-xs font-mono">Q {Math.min(qIndex + 1, campaign.qualitative_questions.length)} / {campaign.qualitative_questions.length}</div>
        </div>
      </div>

      {/* Stimulus */}
      {campaign.stimulus_type !== "none" && campaign.stimulus_url && (
        <div className="px-6 py-4 bg-white border-b border-[var(--rl-border)]" data-testid="stimulus-container">
          <div className="max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)] mb-2">Please review the asset before answering</div>
            {campaign.stimulus_type === "image" && <img src={campaign.stimulus_url} alt="Stimulus" className="max-h-64 mx-auto border border-[var(--rl-border)]" data-testid="stimulus-image" />}
            {campaign.stimulus_type === "video" && <video src={campaign.stimulus_url} controls className="max-h-64 mx-auto border border-[var(--rl-border)]" data-testid="stimulus-video" />}
            {campaign.stimulus_type === "figma" && <iframe src={campaign.stimulus_url} className="w-full h-72 border border-[var(--rl-border)]" title="Figma prototype" data-testid="stimulus-figma" />}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-2xl mx-auto space-y-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "ai" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] px-4 py-3 ${m.role === "ai" ? "bg-white border border-[var(--rl-border)]" : "bg-[var(--rl-text)] text-white"}`} data-testid={`chat-msg-${m.role}-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-60">{m.role === "ai" ? "Interviewer" : "You"}</div>
                <div className="mt-1 text-sm leading-relaxed">{m.text}</div>
              </div>
            </div>
          ))}
          {sending && <div className="text-xs text-[var(--rl-text-secondary)] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Interviewer is thinking…</div>}
        </div>
      </div>

      {/* Video preview (only when video_enabled) */}
      {campaign?.video_enabled && (
        <div className="px-6 bg-black" data-testid="video-preview-strip">
          <div className="max-w-2xl mx-auto py-4 flex items-center justify-center">
            <div className="relative rounded-sm overflow-hidden bg-black border border-white/10" style={{ width: 200, height: 150 }}>
              <video ref={videoPreviewRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="video-preview" />
              {recording && (
                <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-[var(--rl-accent)] text-white text-[9px] uppercase tracking-[0.2em] font-bold">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> REC
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Wave */}
      <div className="px-6 bg-white border-t border-[var(--rl-border)]">
        <div className="max-w-2xl mx-auto py-3">
          <AudioWave analyser={analyserRef.current} active={recording} height={56} />
        </div>
      </div>

      {/* Composer */}
      <div className="px-6 py-5 bg-white border-t border-[var(--rl-border)]" data-testid="composer-area">
        <div className="max-w-2xl mx-auto">
          {error && <div className="text-xs text-[var(--rl-accent)] mb-2" data-testid="composer-error">{error}</div>}
          <div className="flex items-end gap-3">
            {hasMic ? (
              <HoldToSpeak
                active={recording}
                disabled={sending}
                onStart={startRecording}
                onEnd={stopRecording}
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-[var(--rl-bg-subtle)] border border-[var(--rl-border)] flex items-center justify-center text-[var(--rl-text-secondary)]" data-testid="mic-denied-indicator">
                <MicOff className="w-8 h-8" />
              </div>
            )}
            <div className="flex-1">
              <textarea
                rows={3}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={handlePaste}
                placeholder={hasMic ? "Or type your answer here…" : "Type your answer here…"}
                className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] text-sm resize-none"
                data-testid="composer-textarea"
              />
              <button
                onClick={() => sendUserMessage(draft)}
                disabled={sending || !draft.trim()}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[var(--rl-text)] text-white py-2.5 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50 text-sm"
                data-testid="composer-send-btn"
              >
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--rl-text-secondary)] mt-3">{hasMic ? "Hold the button to record · release to send" : "Voice unavailable — type your answer"}</p>
        </div>
      </div>
    </div>
  );
}
